import sanitize from "sanitize-html";
import {
  LearningPathAssignmentIcon,
  LearningPathCourseIcon,
  LearningPathDurationIcon,
  LearningPathLevelIcon,
  LearningPathLineIcon,
  NewVideoIcon,
} from "../svgcomponents/lp-common-icons";
import React, { useEffect, useState } from "react";
import { convertMinutesToHoursAndMinutes } from "@/utils/convert-to-hours-and-minutes";
import Image from "next/image";
import { useAppContext } from "@/context/appContext";
import useAuth from "@/utils/hooks/login-auth";
import { useRouter } from "next/router";
import useUpdateLearningPath from "@/utils/update-learning-path";
import updateUserActivity from "@/utils/update-user-activity";
import { ShareIcon } from "../svgcomponents/popover-icons";
import defaultImageUrl from "../../../public/static/defaultimage.jpeg";
import {
  BookMarkStarFillIcon,
  BookMarkStarIcon,
  TickFillIcon,
  TiltedPinIcon,
} from "../svgcomponents/common-icons";
import ShareModal from "../modal/share-modal";
import { extractPlainText } from "@/utils/rte-converter";
import Tooltip from "../tooltip-custom/CustomTooltip";
import { getDurationinHrs } from "@/utils/getDurationinHrs";

export const Card = ({
  // Original props
  backgroundImage,
  title,
  description,
  href,
  buttonTitle = "Learn More",
  metadata,
  totalCourses,
  isCompleted,
  progress,
  type,
  duration,
  level,
  isLearningPath,
  handleBookmarkLesson,
  cardData,
  uidProp,
  isProfileSection = false,
  isBookmarked,
  totalQuiz,
  // Card size props
  size = "md",
  className = "",
  // Additional props
  onClick,
  children,
  customStyle = {},
  isLpList = false,
}) => {
  const { setShareUrl } = useAppContext();
  const [bookmark, setBookmark] = useState(isBookmarked);
  const { isAuthenticated } = useAuth();
  const router = useRouter();
  const { mutate } = useUpdateLearningPath(isLearningPath);
  const certificates_badges =
    cardData?.learning_pathway_details?.awards_certifications;
  const htmlContent = cardData?.descriptiondata || description;
  // Extract plain text
  const plainText = extractPlainText(htmlContent);

  useEffect(() => {
    setBookmark(isBookmarked);
  }, [isBookmarked]);
  const handleNavigate = () => {
    if (href) {
      window.location.href = href;
    }
    if (onClick) {
      onClick();
    }
  };

  const handleBookmark = (uid, type) => {
    try {
      if (!isAuthenticated) {
        router.push("/login");
      }
      if (isProfileSection) {
        handleBookmarkLesson();
      } else {
        setBookmark(!bookmark);
        const requestData = { isBookmarked: !bookmark };
        const contentTypesUsingUpdate = [
          "Course",
          "Webinar",
          "Explainer",
          "Demo",
        ];

        if (
          contentTypesUsingUpdate.includes(type) ||
          type === "Course" ||
          (type !== "LearningPath" &&
            type !== "learning-path" &&
            type !== "Learning Path" &&
            type !== "Lesson")
        ) {
          updateUserActivity({
            courseEntryUid: uid || uidProp,
            courseActivity: requestData,
          }).catch((error) => {
            setBookmark(bookmark);
            console.error("Error updating bookmark:", error);
          });
        } else {
          mutate({ uid: uid || uidProp, requestData });
        }
      }
    } catch (error) {
      setBookmark(bookmark);
      console.error("Error updating bookmark:", error);
    }
  };

  const handleClick = (btnType) => {
    if (btnType === "share") {
      const modal = document.getElementById("share-modal");
      if (modal) {
        const modalInstance = bootstrap.Modal.getOrCreateInstance(modal);
        modalInstance.show();
      }
      let shareUrl;
      if (href) {
        shareUrl = href;
      } else {
        shareUrl = `/academy/learning-paths/${cardData?.url}`.replace(
          /\/+/g,
          "/"
        );
      }
      const fullShareUrl = `${window.location.origin}${shareUrl}`;
      navigator.clipboard.writeText(fullShareUrl);
      setShareUrl(fullShareUrl);
    }
  };
  const handlePopoverClick = (e, option) => {
    e.stopPropagation();
    e.preventDefault();
    if (option === "share") {
      handleClick("share");
    } else {
      handleBookmark(cardData?.uid, type);
    }
  };

  const handleKeyActivate = (e, handler) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handler(e);
    }
  };
  const CtaIcons = () => {
    return (
      <div
        className=" position-absolute d-flex gap-1 fs-8"
        style={{
          zIndex: 10,
          top: "10px",
          right: "10px",
        }}
      >
        <div className="curriculum-list-card__cta ">
          <div
            className="radius-4 bg-light curriculum-list-card__cta-btns outline-focussed"
            onClick={(e) => handlePopoverClick(e, "bookmark")}
            onKeyDown={(e) =>
              handleKeyActivate(e, (evt) => handlePopoverClick(evt, "bookmark"))
            }
            role="button"
            tabIndex={0}
            aria-label={bookmark ? "Remove Bookmark" : "Bookmark"}
            aria-pressed={!!bookmark}
          >
            <Tooltip
              position="bottom"
              text={isAuthenticated ? "Bookmark" : "Login to Bookmark"}
            >
              {" "}
              {bookmark ? <BookMarkStarFillIcon /> : <BookMarkStarIcon />}
            </Tooltip>
          </div>
          <div
            className="radius-4 bg-light curriculum-list-card__cta-btns outline-focussed"
            onClick={(e) => handlePopoverClick(e, "share")}
            onKeyDown={(e) =>
              handleKeyActivate(e, (evt) => handlePopoverClick(evt, "share"))
            }
            role="button"
            tabIndex={0}
            aria-label="Share"
            data-bs-toggle="modal"
            data-bs-target="#share-modal"
          >
            <Tooltip
              text="Share"
              position="bottom"
            >
              <ShareIcon />
            </Tooltip>
          </div>
        </div>
      </div>
    );
  };

  const renderLearningPathStats = () => {
    const stats = [];
    if (totalCourses) {
      stats.push({
        icon: <LearningPathCourseIcon />,
        text: `${totalCourses} Courses`,
      });
    }
    if (totalQuiz) {
      stats.push({
        icon: <LearningPathAssignmentIcon />,
        text: `${totalQuiz} ${totalQuiz > 1 ? "Quizzes" : "Quiz"}`,
      });
    }
    if (duration) {
      stats.push({
        icon: <LearningPathDurationIcon />,
        text: isLpList
          ? getDurationinHrs(duration)
          : convertMinutesToHoursAndMinutes(duration, typeof duration),
      });
    }
    if (level && level.trim() !== "") {
      stats.push({ icon: <LearningPathLevelIcon />, text: level });
    }

    return (
      <div
        className="d-flex m-0 p-0 align-items-center justify-content-start gap-2 text-grayish-blue"
        style={{ fontSize: "11px", fontWeight: "400" }}
      >
        {stats.map((stat, index) => (
          <div
            key={index}
            // className="admy-learning-card"
            className="d-flex justify-content-center gap-1 align-items-center fs-12 card-stat"
          >
            <div className="d-flex justify-content-center gap-1 align-items-center fs-12 card-stat">
              {stat.icon}
              <span>{stat.text}</span>
            </div>
            {index < stats.length - 1 && <LearningPathLineIcon />}
          </div>
        ))}
      </div>
    );
  };

  const renderLearningPathCard = () => (
    <div
      className={`admy-learning-card ${className} outline-focussed`}
      onClick={handleNavigate}
      onKeyDown={(e) => handleKeyActivate(e, handleNavigate)}
      role="article"
      tabIndex={0}
      aria-label={`${title || cardData?.title} learning path`}
      style={customStyle}
    >
      <div className="card position-relative">
        {certificates_badges?.includes("badge") && (
          <div
            className="admy-learning-card-badge"
            style={{
              zIndex: 10,
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.00241 9.37895C6.14912 9.37895 4.64232 7.91144 4.64232 6.10648C4.64232 4.30152 6.14912 2.83402 8.00241 2.83402C9.85571 2.83402 11.3625 4.30152 11.3625 6.10648C11.3625 7.91144 9.85571 9.37895 8.00241 9.37895ZM8.00241 3.61941C6.59432 3.61941 5.44874 4.73511 5.44874 6.10648C5.44874 7.47786 6.59432 8.59356 8.00241 8.59356C9.41051 8.59356 10.5561 7.47786 10.5561 6.10648C10.5561 4.73511 9.41051 3.61941 8.00241 3.61941ZM14.6691 12.6462L12.6583 9.25407C12.8442 8.93091 12.8263 8.55049 12.8084 8.21097C12.7979 7.99826 12.7853 7.77738 12.8337 7.63522C12.8767 7.50535 13.0153 7.33968 13.1487 7.18014C13.3965 6.88256 13.6779 6.54609 13.6779 6.10635C13.6779 5.66559 13.3965 5.32913 13.1487 5.03256C13.0153 4.87303 12.8778 4.70838 12.8337 4.57748C12.7864 4.43533 12.798 4.21445 12.8084 4.00174C12.8274 3.62643 12.8494 3.20101 12.5932 2.8574C12.3338 2.51175 11.9096 2.40027 11.5348 2.30312C11.3248 2.2479 11.1074 2.19165 10.9866 2.10575C10.869 2.02189 10.7483 1.83987 10.6317 1.66499C10.4196 1.34591 10.1792 0.983895 9.75601 0.849933C9.34756 0.721079 8.9433 0.871409 8.58522 1.00435C8.37731 1.08207 8.16206 1.16184 8.00139 1.16184C7.84179 1.16184 7.62654 1.08207 7.41757 1.00435C7.06057 0.871408 6.65526 0.721087 6.24678 0.849933C5.82362 0.9839 5.58315 1.34591 5.37106 1.66499C5.25451 1.84088 5.13375 2.02189 5.01615 2.10677C4.8954 2.19267 4.67804 2.24892 4.46802 2.30414C4.09317 2.40129 3.66894 2.51276 3.40959 2.85842C3.15339 3.20203 3.17544 3.62745 3.19434 4.00276C3.20484 4.21547 3.21744 4.43635 3.16914 4.5785C3.12609 4.70838 2.98748 4.87405 2.85412 5.03359C2.60527 5.33015 2.32385 5.66763 2.32385 6.10737C2.32385 6.54813 2.60526 6.8846 2.85307 7.18116C2.98643 7.34069 3.12398 7.50534 3.16809 7.63624C3.21534 7.77941 3.20379 7.99928 3.19329 8.21301C3.17544 8.55253 3.15759 8.93193 3.34449 9.25509L1.33368 12.6482C1.25913 12.774 1.26123 12.9284 1.33998 13.0512C1.41873 13.1739 1.56154 13.2455 1.70854 13.2363L3.85795 13.1013L4.81244 14.9819C4.87859 15.1118 5.01194 15.1957 5.16105 15.2008H5.17365C5.31751 15.2008 5.45085 15.1261 5.52332 15.0044L7.85544 11.0715C7.90689 11.0612 7.95519 11.0541 7.9993 11.0541C8.0434 11.0541 8.09275 11.0612 8.14315 11.0715L10.4753 15.0044C10.5477 15.1261 10.6811 15.2008 10.8249 15.2008H10.8375C10.9866 15.1957 11.12 15.1118 11.1862 14.9819L12.1406 13.1013L14.2901 13.2363C14.4381 13.2455 14.5799 13.1749 14.6586 13.0512C14.7374 12.9285 14.7395 12.773 14.6649 12.6482L14.6691 12.6462ZM5.2052 13.9736L4.46493 12.5163C4.39248 12.3742 4.24022 12.2893 4.07747 12.2985L2.41108 12.4028L3.97877 9.75811C4.14047 9.82254 4.30953 9.86753 4.46912 9.90946C4.67913 9.96469 4.89647 10.0209 5.01725 10.1068C5.13485 10.1907 5.25561 10.3717 5.37216 10.5476C5.58426 10.8677 5.82472 11.2297 6.24788 11.3626C6.40958 11.4138 6.57128 11.4199 6.7288 11.4015L5.20415 13.9724L5.2052 13.9736ZM7.49845 10.3503C7.4848 10.3534 7.4722 10.3585 7.4596 10.3626C7.3462 10.3973 7.23595 10.4362 7.13198 10.4751C6.88837 10.5661 6.6353 10.6602 6.49775 10.6162C6.34655 10.5681 6.19639 10.3411 6.05043 10.1212C5.89398 9.885 5.73122 9.64162 5.49287 9.47184C5.25136 9.30004 4.9605 9.22436 4.67911 9.15072C4.45545 9.09243 4.2255 9.03209 4.10894 8.93699C4.10474 8.93289 4.10159 8.9288 4.09634 8.92471C4.08479 8.91449 4.07219 8.90324 4.06379 8.89097C3.97559 8.77234 3.98924 8.50644 4.00184 8.24875C4.01654 7.96447 4.03229 7.66994 3.93883 7.39076C3.84853 7.1208 3.66269 6.89785 3.48313 6.6831C3.31197 6.47857 3.13451 6.26585 3.13451 6.10531C3.13451 5.94478 3.31197 5.73204 3.48313 5.52651C3.66373 5.31175 3.84959 5.08779 3.93883 4.81884C4.03229 4.53864 4.01654 4.24514 4.00184 3.96085C3.98819 3.70315 3.97454 3.43726 4.06379 3.31864C4.15514 3.19695 4.42079 3.1274 4.67807 3.05991C4.95948 2.98628 5.25033 2.9106 5.49183 2.73879C5.73124 2.56903 5.89295 2.32565 6.04939 2.0894C6.19535 1.86953 6.34656 1.64251 6.49672 1.59445C6.63427 1.55047 6.88627 1.64456 7.12989 1.73557C7.39345 1.83375 7.69272 1.94521 8.00037 1.94521C8.30907 1.94521 8.60729 1.83375 8.87085 1.73557C9.11446 1.64456 9.36648 1.55047 9.50403 1.59445C9.65523 1.64251 9.80539 1.86954 9.95135 2.0894C10.1078 2.32563 10.2706 2.57004 10.5089 2.73879C10.7515 2.91059 11.0413 2.98627 11.3227 3.05991C11.5799 3.1274 11.8466 3.19592 11.937 3.31762C12.0252 3.43624 12.0115 3.70317 11.9989 3.95983C11.9842 4.24412 11.9685 4.53864 12.0619 4.81782C12.1522 5.08779 12.3381 5.31073 12.5176 5.52548C12.6888 5.73001 12.8662 5.94274 12.8662 6.10327C12.8662 6.26485 12.6888 6.47654 12.5176 6.68106C12.337 6.89684 12.1511 7.11978 12.0619 7.38872C11.9685 7.66892 11.9842 7.96242 11.9989 8.24671C12.0126 8.50442 12.0262 8.7703 11.937 8.88892C11.8456 9.01062 11.5799 9.08016 11.3227 9.14765C11.0413 9.22026 10.7515 9.29696 10.5089 9.46877C10.2695 9.63853 10.1078 9.88192 9.95135 10.1182C9.8054 10.338 9.65418 10.565 9.50403 10.6131C9.36647 10.6571 9.11447 10.563 8.87085 10.472C8.7669 10.4331 8.65664 10.3943 8.54323 10.3595C8.52958 10.3554 8.51698 10.3503 8.50333 10.3472C8.34057 10.2992 8.17256 10.2634 7.99931 10.2634C7.82607 10.2634 7.65806 10.2992 7.4953 10.3472L7.49845 10.3503ZM11.9276 12.2984C11.7669 12.2892 11.6126 12.3741 11.5401 12.5163L10.7999 13.9735L9.27417 11.3995C9.32562 11.4057 9.37707 11.4128 9.42852 11.4128C9.53668 11.4128 9.64693 11.3985 9.7572 11.3637C10.1804 11.2298 10.4208 10.8678 10.6329 10.5487C10.7495 10.3728 10.8702 10.1918 10.9878 10.1079C11.1086 10.022 11.3259 9.96578 11.5349 9.91055C11.6956 9.86862 11.8646 9.82261 12.0253 9.7592L13.5929 12.4039L11.9266 12.2996L11.9276 12.2984Z"
                fill="black"
              />
            </svg>
            <span className="admy-learning-card-badge-title">Skill Badge</span>
          </div>
        )}
        {certificates_badges?.includes("certificate") && (
          <div
            className="admy-learning-card-badge"
            style={{
              zIndex: 10,
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.00241 9.37895C6.14912 9.37895 4.64232 7.91144 4.64232 6.10648C4.64232 4.30152 6.14912 2.83402 8.00241 2.83402C9.85571 2.83402 11.3625 4.30152 11.3625 6.10648C11.3625 7.91144 9.85571 9.37895 8.00241 9.37895ZM8.00241 3.61941C6.59432 3.61941 5.44874 4.73511 5.44874 6.10648C5.44874 7.47786 6.59432 8.59356 8.00241 8.59356C9.41051 8.59356 10.5561 7.47786 10.5561 6.10648C10.5561 4.73511 9.41051 3.61941 8.00241 3.61941ZM14.6691 12.6462L12.6583 9.25407C12.8442 8.93091 12.8263 8.55049 12.8084 8.21097C12.7979 7.99826 12.7853 7.77738 12.8337 7.63522C12.8767 7.50535 13.0153 7.33968 13.1487 7.18014C13.3965 6.88256 13.6779 6.54609 13.6779 6.10635C13.6779 5.66559 13.3965 5.32913 13.1487 5.03256C13.0153 4.87303 12.8778 4.70838 12.8337 4.57748C12.7864 4.43533 12.798 4.21445 12.8084 4.00174C12.8274 3.62643 12.8494 3.20101 12.5932 2.8574C12.3338 2.51175 11.9096 2.40027 11.5348 2.30312C11.3248 2.2479 11.1074 2.19165 10.9866 2.10575C10.869 2.02189 10.7483 1.83987 10.6317 1.66499C10.4196 1.34591 10.1792 0.983895 9.75601 0.849933C9.34756 0.721079 8.9433 0.871409 8.58522 1.00435C8.37731 1.08207 8.16206 1.16184 8.00139 1.16184C7.84179 1.16184 7.62654 1.08207 7.41757 1.00435C7.06057 0.871408 6.65526 0.721087 6.24678 0.849933C5.82362 0.9839 5.58315 1.34591 5.37106 1.66499C5.25451 1.84088 5.13375 2.02189 5.01615 2.10677C4.8954 2.19267 4.67804 2.24892 4.46802 2.30414C4.09317 2.40129 3.66894 2.51276 3.40959 2.85842C3.15339 3.20203 3.17544 3.62745 3.19434 4.00276C3.20484 4.21547 3.21744 4.43635 3.16914 4.5785C3.12609 4.70838 2.98748 4.87405 2.85412 5.03359C2.60527 5.33015 2.32385 5.66763 2.32385 6.10737C2.32385 6.54813 2.60526 6.8846 2.85307 7.18116C2.98643 7.34069 3.12398 7.50534 3.16809 7.63624C3.21534 7.77941 3.20379 7.99928 3.19329 8.21301C3.17544 8.55253 3.15759 8.93193 3.34449 9.25509L1.33368 12.6482C1.25913 12.774 1.26123 12.9284 1.33998 13.0512C1.41873 13.1739 1.56154 13.2455 1.70854 13.2363L3.85795 13.1013L4.81244 14.9819C4.87859 15.1118 5.01194 15.1957 5.16105 15.2008H5.17365C5.31751 15.2008 5.45085 15.1261 5.52332 15.0044L7.85544 11.0715C7.90689 11.0612 7.95519 11.0541 7.9993 11.0541C8.0434 11.0541 8.09275 11.0612 8.14315 11.0715L10.4753 15.0044C10.5477 15.1261 10.6811 15.2008 10.8249 15.2008H10.8375C10.9866 15.1957 11.12 15.1118 11.1862 14.9819L12.1406 13.1013L14.2901 13.2363C14.4381 13.2455 14.5799 13.1749 14.6586 13.0512C14.7374 12.9285 14.7395 12.773 14.6649 12.6482L14.6691 12.6462ZM5.2052 13.9736L4.46493 12.5163C4.39248 12.3742 4.24022 12.2893 4.07747 12.2985L2.41108 12.4028L3.97877 9.75811C4.14047 9.82254 4.30953 9.86753 4.46912 9.90946C4.67913 9.96469 4.89647 10.0209 5.01725 10.1068C5.13485 10.1907 5.25561 10.3717 5.37216 10.5476C5.58426 10.8677 5.82472 11.2297 6.24788 11.3626C6.40958 11.4138 6.57128 11.4199 6.7288 11.4015L5.20415 13.9724L5.2052 13.9736ZM7.49845 10.3503C7.4848 10.3534 7.4722 10.3585 7.4596 10.3626C7.3462 10.3973 7.23595 10.4362 7.13198 10.4751C6.88837 10.5661 6.6353 10.6602 6.49775 10.6162C6.34655 10.5681 6.19639 10.3411 6.05043 10.1212C5.89398 9.885 5.73122 9.64162 5.49287 9.47184C5.25136 9.30004 4.9605 9.22436 4.67911 9.15072C4.45545 9.09243 4.2255 9.03209 4.10894 8.93699C4.10474 8.93289 4.10159 8.9288 4.09634 8.92471C4.08479 8.91449 4.07219 8.90324 4.06379 8.89097C3.97559 8.77234 3.98924 8.50644 4.00184 8.24875C4.01654 7.96447 4.03229 7.66994 3.93883 7.39076C3.84853 7.1208 3.66269 6.89785 3.48313 6.6831C3.31197 6.47857 3.13451 6.26585 3.13451 6.10531C3.13451 5.94478 3.31197 5.73204 3.48313 5.52651C3.66373 5.31175 3.84959 5.08779 3.93883 4.81884C4.03229 4.53864 4.01654 4.24514 4.00184 3.96085C3.98819 3.70315 3.97454 3.43726 4.06379 3.31864C4.15514 3.19695 4.42079 3.1274 4.67807 3.05991C4.95948 2.98628 5.25033 2.9106 5.49183 2.73879C5.73124 2.56903 5.89295 2.32565 6.04939 2.0894C6.19535 1.86953 6.34656 1.64251 6.49672 1.59445C6.63427 1.55047 6.88627 1.64456 7.12989 1.73557C7.39345 1.83375 7.69272 1.94521 8.00037 1.94521C8.30907 1.94521 8.60729 1.83375 8.87085 1.73557C9.11446 1.64456 9.36648 1.55047 9.50403 1.59445C9.65523 1.64251 9.80539 1.86954 9.95135 2.0894C10.1078 2.32563 10.2706 2.57004 10.5089 2.73879C10.7515 2.91059 11.0413 2.98627 11.3227 3.05991C11.5799 3.1274 11.8466 3.19592 11.937 3.31762C12.0252 3.43624 12.0115 3.70317 11.9989 3.95983C11.9842 4.24412 11.9685 4.53864 12.0619 4.81782C12.1522 5.08779 12.3381 5.31073 12.5176 5.52548C12.6888 5.73001 12.8662 5.94274 12.8662 6.10327C12.8662 6.26485 12.6888 6.47654 12.5176 6.68106C12.337 6.89684 12.1511 7.11978 12.0619 7.38872C11.9685 7.66892 11.9842 7.96242 11.9989 8.24671C12.0126 8.50442 12.0262 8.7703 11.937 8.88892C11.8456 9.01062 11.5799 9.08016 11.3227 9.14765C11.0413 9.22026 10.7515 9.29696 10.5089 9.46877C10.2695 9.63853 10.1078 9.88192 9.95135 10.1182C9.8054 10.338 9.65418 10.565 9.50403 10.6131C9.36647 10.6571 9.11447 10.563 8.87085 10.472C8.7669 10.4331 8.65664 10.3943 8.54323 10.3595C8.52958 10.3554 8.51698 10.3503 8.50333 10.3472C8.34057 10.2992 8.17256 10.2634 7.99931 10.2634C7.82607 10.2634 7.65806 10.2992 7.4953 10.3472L7.49845 10.3503ZM11.9276 12.2984C11.7669 12.2892 11.6126 12.3741 11.5401 12.5163L10.7999 13.9735L9.27417 11.3995C9.32562 11.4057 9.37707 11.4128 9.42852 11.4128C9.53668 11.4128 9.64693 11.3985 9.7572 11.3637C10.1804 11.2298 10.4208 10.8678 10.6329 10.5487C10.7495 10.3728 10.8702 10.1918 10.9878 10.1079C11.1086 10.022 11.3259 9.96578 11.5349 9.91055C11.6956 9.86862 11.8646 9.82261 12.0253 9.7592L13.5929 12.4039L11.9266 12.2996L11.9276 12.2984Z"
                fill="black"
              />
            </svg>
            <span className="admy-learning-card-badge-title">Certificate</span>
          </div>
        )}
        {<CtaIcons />}
        <Image
          src={
            backgroundImage ||
            cardData?.learning_pathway_details?.thumbnail?.url ||
            defaultImageUrl
          }
          alt={`${title || cardData?.title} thumbnail`}
          width={320}
          height={160}
          className="card-img-top"
          loading="lazy"
          placeholder="blur"
          blurDataURL={`data:image/svg+xml;base64,${btoa(`
    <svg width="200" height="122" viewBox="0 0 200 122" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="shimmer" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stop-color="#f0f0f0">
            <animate attributeName="offset" values="-2; 1" dur="2s" repeatCount="indefinite"/>
          </stop>
          <stop offset="50%" stop-color="#f7f7f7">
            <animate attributeName="offset" values="-1.5; 1.5" dur="2s" repeatCount="indefinite"/>
          </stop>
          <stop offset="100%" stop-color="#f0f0f0">
            <animate attributeName="offset" values="-1; 2" dur="2s" repeatCount="indefinite"/>
          </stop>
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="#e0e0e0"/>
      <rect width="100%" height="100%" fill="url(#shimmer)"/>
    </svg>
  `)}}`}
        />
        <div
          className="card-body text-secondary"
          style={{ padding: "16px" }}
        >
          {renderLearningPathStats()}
          <div className="d-flex flex-column justify-content-center align-items-start cs-gap-4 mt-3 fs-8 admy-learning-card-body">
            <h3 className="fw-semi-bold mb-0 card-title-learning ">
              {title || cardData?.title}
            </h3>
            <div
              className="w-100"
              style={{
                height: "100px",
              }}
            >
              <p
                className="card-desc-learning"
                style={{
                  WebkitLineClamp: progress && !isCompleted ? 1 : 3,
                }}
              >
                {plainText || cardData?.learning_pathway_details?.description}
              </p>
              {progress && !isCompleted && isAuthenticated ? (
                <div className="card-footer-learning w-100">
                  <div className="d-flex flex-column justify-content-center text-secondary fw-lighter gap-2 align-items-start w-100">
                    <div
                      className="border"
                      style={{
                        height: "6px",
                        borderRadius: "10px",
                        overflow: "hidden",
                        width: "100%",
                        margin: "auto",
                      }}
                    >
                      <div
                        className="curriculum-list-card__progress-bar-progress"
                        style={{ width: `${progress}%`, height: "100%" }}
                        role="progressbar"
                        aria-valuenow={progress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                        aria-label={`${Math.ceil(progress)}% completed`}
                      />
                    </div>
                    <div
                      className="d-flex gap-1 align-items-center justify-content-start fs-12"
                      style={{ width: "100%", margin: "auto" }}
                    >
                      <NewVideoIcon />
                      <span className="curriculum-list-card-body-progress">
                        {Math.ceil(progress)}% completed
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="card-footer-learning w-100"></div>
              )}
            </div>
            {isCompleted && isAuthenticated ? (
              <div className={`card-footer-learning `}>
                {" "}
                <a
                  className="d-flex align-items-start fw-normal fs-12 text-grayish-blue p-0 m-0"
                  href={href}
                  aria-label="Completed"
                >
                  <TickFillIcon />
                  <span className="curriculum-list-card-body-progress">
                    Completed
                  </span>
                </a>{" "}
              </div>
            ) : progress && !isCompleted && isAuthenticated ? (
              <div
                className={`card-footer-learning `}
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <a
                  className="body-5 fw-bold stretched-link lh-21"
                  href={href}
                  aria-label={`${buttonTitle} - ${title}`}
                >
                  {buttonTitle && (
                    <p className="link-default text-primary">Resume Learning</p>
                  )}
                </a>
              </div>
            ) : (
              <div
                className={`card-footer-learning `}
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <a
                  className="body-5 fw-bold stretched-link lh-21"
                  href={href}
                  aria-label={`${buttonTitle} - ${title}`}
                >
                  {buttonTitle && (
                    <p className="link-default text-primary">{buttonTitle}</p>
                  )}
                </a>
              </div>
            )}
          </div>
        </div>
        {children}
      </div>
    </div>
  );

  const renderStandardCard = () => (
    <div className="card-with-image size-md admy-card h-100">
      <div className="card cs-resource h-100 ">
        {/* <div className="card-wrap"> */}

        <div
          className="card-img-top w-full"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            width: "100%",
            height: "100%",
          }}
        />
        {/* </div> */}
        <div className="card-body text-stone-300 pb-4">
          <div className="mb-3 d-flex justify-content-between">
            <div className="d-flex flex-wrap align-items-center">
              <div className="wrap">
                {type ? (
                  <>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M7.91943 6.75788C8.50277 6.42109 8.50277 5.57913 7.91943 5.24234L5.23339 3.69155C4.65005 3.35476 3.92089 3.77574 3.92089 4.44932L3.92089 7.5509C3.92089 8.22447 4.65005 8.64546 5.23339 8.30867L7.91943 6.75788ZM7.54443 5.89186C7.62777 5.93997 7.62777 6.06025 7.54443 6.10836L4.85839 7.65915C4.77505 7.70726 4.67089 7.64712 4.67089 7.5509V4.44932C4.67089 4.35309 4.77505 4.29295 4.85839 4.34107L7.54443 5.89186Z"
                        fill="#475161"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.917969 0.0437012C0.434719 0.0437012 0.0429688 0.435453 0.0429688 0.918702V11.0815C0.0429688 11.5647 0.43472 11.9565 0.917969 11.9565H11.0808C11.564 11.9565 11.9558 11.5647 11.9558 11.0815V0.918701C11.9558 0.435452 11.564 0.0437012 11.0808 0.0437012H0.917969ZM0.792969 0.918702C0.792969 0.849666 0.848933 0.793701 0.917969 0.793701H11.0808C11.1498 0.793701 11.2058 0.849666 11.2058 0.918701V11.0815C11.2058 11.1505 11.1498 11.2065 11.0808 11.2065H0.917969C0.848933 11.2065 0.792969 11.1505 0.792969 11.0815V0.918702Z"
                        fill="#475161"
                      />
                    </svg>
                    <span className="mx-2 me-3 body-6">{type}</span>
                  </>
                ) : (
                  ""
                )}
              </div>

              <div className="wrap">
                {duration && (
                  <>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.375 3.75C5.375 3.54289 5.54289 3.375 5.75 3.375C5.95711 3.375 6.125 3.54289 6.125 3.75V6.0625L7.975 7.45C8.14069 7.57426 8.17426 7.80931 8.05 7.975C7.92574 8.14069 7.69069 8.17426 7.525 8.05L5.525 6.55C5.43057 6.47918 5.375 6.36803 5.375 6.25V3.75Z"
                        fill="#475161"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12 6C12 9.31371 9.31371 12 6 12C2.68629 12 0 9.31371 0 6C0 2.68629 2.68629 0 6 0C9.31371 0 12 2.68629 12 6ZM11.25 6C11.25 8.8995 8.8995 11.25 6 11.25C3.10051 11.25 0.75 8.8995 0.75 6C0.75 3.10051 3.10051 0.75 6 0.75C8.8995 0.75 11.25 3.10051 11.25 6Z"
                        fill="#475161"
                      />
                    </svg>

                    <span
                      className="ms-2 body-6"
                      style={{ lineHeight: "18px", color: "#212121" }}
                    >
                      {duration}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <h3
            className="mb-3 body-5 fw-semi-bold card-desc"
            dangerouslySetInnerHTML={{ __html: sanitize(title) }}
            style={{ color: "#475161" }}
            tabIndex={0}
            aria-label={title}
          />
        </div>
        {href && (
          <div className={`card-footer ${buttonTitle ? "pb-4" : ""}`}>
            <a
              className="body-5 fw-bold stretched-link"
              href={href}
              aria-label={`${buttonTitle} - ${title}`}
            >
              {buttonTitle && (
                <span className="link-default">{buttonTitle}</span>
              )}
            </a>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>{isLearningPath ? renderLearningPathCard() : renderStandardCard()}</>
  );
};
